import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Upload, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = '';  // Use relative URLs

const CreatorOnboarding = ({ isOpen = true, onClose = () => {}, onComplete = () => {}, onCancel = null, redirectTo, user }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [handleChecking, setHandleChecking] = useState(false);
  const [handleAvailable, setHandleAvailable] = useState(true);

  const [formData, setFormData] = useState({
    displayName: '',
    handle: '',
    avatar: null,
    banner: null,
    bio: '',
    mainContentTypes: [],
    links: {
      website: '',
      instagram: '',
      twitter: '',
      youtube: ''
    },
    agreements: {
      ownsRights: false,
      termsAccepted: false
    }
  });

  // Load saved data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('creatorOnboardingDraft');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFormData(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    if (formData.displayName || formData.handle || formData.bio) {
      localStorage.setItem('creatorOnboardingDraft', JSON.stringify(formData));
    }
  }, [formData]);

  // Auto-suggest handle from display name
  useEffect(() => {
    if (formData.displayName && !formData.handle) {
      const suggested = formData.displayName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20);
      setFormData(prev => ({ ...prev, handle: suggested }));
    }
  }, [formData.displayName]);

  // Check handle availability
  useEffect(() => {
    const checkHandle = async () => {
      if (formData.handle && formData.handle.length >= 3) {
        setHandleChecking(true);
        try {
          const response = await axios.get('/api/creator/check-handle/${formData.handle}');
          setHandleAvailable(response.data.available);
        } catch (error) {
          setHandleAvailable(false);
        } finally {
          setHandleChecking(false);
        }
      }
    };

    const timer = setTimeout(checkHandle, 500);
    return () => clearTimeout(timer);
  }, [formData.handle]);

  const handleFileUpload = async (file, type) => {
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post('/api/upload', formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({
        ...prev,
        [type]: response.data.url
      }));

      toast.success(`${type === 'avatar' ? 'Avatar' : 'Banner'} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${type}`);
    }
  };

  const toggleContentType = (type) => {
    setFormData(prev => ({
      ...prev,
      mainContentTypes: prev.mainContentTypes.includes(type)
        ? prev.mainContentTypes.filter(t => t !== type)
        : [...prev.mainContentTypes, type]
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 2:
        if (!formData.displayName.trim() || formData.displayName.length > 40) {
          toast.error('Display name is required (max 40 characters)');
          return false;
        }
        if (!formData.handle || formData.handle.length < 3 || formData.handle.length > 20) {
          toast.error('Handle must be 3-20 characters');
          return false;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(formData.handle)) {
          toast.error('Handle can only contain letters, numbers, and underscores');
          return false;
        }
        if (!handleAvailable) {
          toast.error('Handle is already taken');
          return false;
        }
        return true;
      case 3:
        if (formData.bio.length > 400) {
          toast.error('Bio must be 400 characters or less');
          return false;
        }
        return true;
      case 4:
        if (!formData.agreements.ownsRights || !formData.agreements.termsAccepted) {
          toast.error('Please accept both agreements to continue');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleFinish = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/creator/complete-onboarding',
        {
          displayName: formData.displayName,
          handle: formData.handle,
          avatarUrl: formData.avatar,
          bannerUrl: formData.banner,
          bio: formData.bio,
          mainContentTypes: formData.mainContentTypes,
          links: formData.links
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Clear draft
      localStorage.removeItem('creatorOnboardingDraft');

      // Show success step
      setCurrentStep(5);

      // Notify parent that onboarding is complete so it can redirect or refresh
      // Update stored user role to creator if present in response
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          parsed.role = 'creator';
          if (response.data?.creator_profile) {
            parsed.creator_profile = response.data.creator_profile;
          }
          localStorage.setItem('user', JSON.stringify(parsed));
        }
      } catch (e) {
        console.error('Failed to update stored user after onboarding', e);
      }

      onComplete(response.data);
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (currentStep < 5) {
      const confirmLeave = window.confirm('Your progress will be saved. Continue later?');
      if (confirmLeave) {
        if (onCancel) {
          onCancel();
        } else {
          onClose();
        }
      }
    } else {
      if (onCancel) {
        onCancel();
      } else {
        onClose();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="bg-gray-900 border-gray-800 max-w-2xl w-full my-8">
        <CardContent className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Creator Onboarding</h2>
              {currentStep < 5 && (
                <p className="text-gray-400 text-sm mt-1">Step {currentStep} of 4</p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          {currentStep < 5 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`flex-1 h-2 rounded-full mx-1 ${
                      step <= currentStep ? 'bg-orange-500' : 'bg-gray-700'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Upload className="w-10 h-10 text-orange-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Welcome to Trap Comix Creator Mode
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed max-w-lg mx-auto">
                  Creators can upload comics, novels, artwork, and more. We just need a few details to set up your creator profile.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={nextStep}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-6"
                >
                  Continue
                </Button>
                <Button
                  onClick={handleClose}
                  variant="ghost"
                  className="flex-1 text-gray-400 hover:text-white"
                >
                  Not now
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Profile Basics */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Profile Basics</h3>
                <p className="text-gray-400">Set up your creator identity</p>
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                  maxLength={40}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Your Name or Studio Name"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.displayName.length}/40</p>
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">
                  Handle <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.handle}
                    onChange={(e) => setFormData(prev => ({ ...prev, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                    maxLength={20}
                    className={`w-full px-4 py-3 bg-gray-800 border ${
                      formData.handle && !handleAvailable ? 'border-red-500' : 'border-gray-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    placeholder="username"
                  />
                  {handleChecking && (
                    <div className="absolute right-3 top-3 text-gray-400">Checking...</div>
                  )}
                  {formData.handle && !handleChecking && (
                    <div className={`absolute right-3 top-3 ${handleAvailable ? 'text-green-500' : 'text-red-500'}`}>
                      {handleAvailable ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  trapcomix.com/u/{formData.handle || 'yourhandle'}
                </p>
                {formData.handle && !handleAvailable && (
                  <p className="text-xs text-red-500 mt-1">Handle already taken</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-semibold mb-2">Avatar (Optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'avatar')}
                    className="sr-only"
                    id="avatar-upload"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="flex items-center justify-center w-full h-32 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-orange-500 transition-colors"
                  >
                    {formData.avatar ? (
                      <img src={formData.avatar} alt="Avatar" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Upload Avatar</p>
                      </div>
                    )}
                  </label>
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Banner (Optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'banner')}
                    className="sr-only"
                    id="banner-upload"
                  />
                  <label
                    htmlFor="banner-upload"
                    className="flex items-center justify-center w-full h-32 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-orange-500 transition-colors"
                  >
                    {formData.banner ? (
                      <img src={formData.banner} alt="Banner" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <p className="text-xs text-gray-500">Upload Banner</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={prevStep} variant="outline" className="flex-1 border-gray-700 text-white">
                  Back
                </Button>
                <Button onClick={nextStep} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Bio & Focus */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Bio & Focus</h3>
                <p className="text-gray-400">Tell us about yourself and what you create</p>
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">Bio (Optional)</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  maxLength={400}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  placeholder="Tell your audience about yourself and your work..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.bio.length}/400</p>
              </div>

              <div>
                <label className="block text-white font-semibold mb-3">Main Content Types</label>
                <div className="grid grid-cols-2 gap-3">
                  {['Comics', 'Novels', 'Artwork', 'Motion Comics'].map((type) => (
                    <button
                      key={type}
                      onClick={() => toggleContentType(type)}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        formData.mainContentTypes.includes(type)
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-white font-semibold mb-3">Social Links (Optional)</label>
                <div className="space-y-3">
                  <input
                    type="url"
                    value={formData.links.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, links: { ...prev.links, website: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Website / Linktree"
                  />
                  <input
                    type="text"
                    value={formData.links.instagram}
                    onChange={(e) => setFormData(prev => ({ ...prev, links: { ...prev.links, instagram: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Instagram username"
                  />
                  <input
                    type="text"
                    value={formData.links.twitter}
                    onChange={(e) => setFormData(prev => ({ ...prev, links: { ...prev.links, twitter: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="X / Twitter handle"
                  />
                  <input
                    type="text"
                    value={formData.links.youtube}
                    onChange={(e) => setFormData(prev => ({ ...prev, links: { ...prev.links, youtube: e.target.value } }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="YouTube / Twitch"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={prevStep} variant="outline" className="flex-1 border-gray-700 text-white">
                  Back
                </Button>
                <Button onClick={nextStep} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Agreements */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Creator Agreements</h3>
                <p className="text-gray-400">Please review and accept our creator terms</p>
              </div>

              <div className="space-y-4">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.agreements.ownsRights}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      agreements: { ...prev.agreements, ownsRights: e.target.checked }
                    }))}
                    className="mt-1 w-5 h-5 rounded border-gray-700 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-gray-300 group-hover:text-white transition-colors">
                    I confirm that I own the rights to the content I upload or have permission to use it.
                  </span>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.agreements.termsAccepted}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      agreements: { ...prev.agreements, termsAccepted: e.target.checked }
                    }))}
                    className="mt-1 w-5 h-5 rounded border-gray-700 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-gray-300 group-hover:text-white transition-colors">
                    I agree to Trap Comix's{' '}
                    <a
                      href="/creator-terms"
                      target="_blank"
                      className="text-orange-500 hover:text-orange-400 inline-flex items-center"
                    >
                      Creator Terms & Revenue Policy
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </span>
                </label>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h4 className="text-white font-semibold mb-2">What happens next?</h4>
                <ul className="text-gray-300 text-sm space-y-1">
                  <li>• Your creator profile will be created</li>
                  <li>• You'll be able to upload comics, novels, and artwork</li>
                  <li>• You can customize your profile anytime in settings</li>
                  <li>• You'll earn Trap Energy and build your audience</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button onClick={prevStep} variant="outline" className="flex-1 border-gray-700 text-white">
                  Back
                </Button>
                <Button
                  onClick={handleFinish}
                  disabled={loading || !formData.agreements.ownsRights || !formData.agreements.termsAccepted}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Setting Up...' : 'Finish Setup'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {currentStep === 5 && (
            <div className="text-center py-8 space-y-6">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <Check className="w-10 h-10 text-green-500" />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  You're officially a Trap Comix Creator!
                </h3>
                <p className="text-gray-300 text-lg">
                  You can now upload comics, novels, and artwork.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => {
                    onClose();
                    if (redirectTo) {
                      navigate(redirectTo);
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-6"
                >
                  Start Uploading
                </Button>
                <Button
                  onClick={() => {
                    onClose();
                    navigate(`/u/${formData.handle}`);
                  }}
                  variant="outline"
                  className="flex-1 border-gray-700 text-white"
                >
                  View My Creator Profile
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatorOnboarding;
